/**
 * MedicalByline — visible author/reviewer attribution for service, guide, and FAQ pages.
 * Renders a slim byline strip linking to the credentials page.
 * Also injects the reusable DR_GJELAJ_PERSON_SCHEMA into any parent page's jsonLd array.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

export const DR_GJELAJ_PERSON_SCHEMA = {
  "@type": "Person",
  "@id": "https://trinitydentalarts.com/best-implant-dentist-trinity-fl#dr-gjelaj",
  "name": "Dr. Xhoana Gjelaj",
  "honorificPrefix": "Dr.",
  "honorificSuffix": "DMD, MAGD, DICOI, FAAID, DABOI/ID",
  "jobTitle": "Board-Certified Implantologist & Lead Dentist",
  "url": "https://trinitydentalarts.com/meet-the-doctor",
  "image": "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/fb403896a_IMG_0190.jpg",
  "sameAs": [
    "https://trinitydentalarts.com/meet-the-doctor",
    "https://aaid-implant.org/dentist/gjelaj-xhoana/",
    "https://agd.org/practice/tools/patient-resources/find-an-agd-dentist/PracticeInfo/?id=0csy%2biH76jQ%3d",
    "https://www.healthgrades.com/dentist/dr-xhoana-gjelaj-xbk4c"
  ],
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "name": "Doctor of Dental Medicine (DMD)",
      "credentialCategory": "Degree",
      "recognizedBy": { "@type": "CollegeOrUniversity", "name": "Nova Southeastern University" }
    },
    {
      "@type": "EducationalOccupationalCredential",
      "name": "Diplomate, American Board of Oral Implantology / Implant Dentistry (DABOI/ID)",
      "credentialCategory": "BoardCertification",
      "recognizedBy": { "@type": "Organization", "name": "American Board of Oral Implantology", "sameAs": "https://www.aboi.org" }
    },
    {
      "@type": "EducationalOccupationalCredential",
      "name": "Master of the Academy of General Dentistry (MAGD)",
      "credentialCategory": "PostGraduateCertificate",
      "recognizedBy": { "@type": "Organization", "name": "Academy of General Dentistry", "sameAs": "https://agd.org" }
    },
    {
      "@type": "EducationalOccupationalCredential",
      "name": "Fellow, American Academy of Implant Dentistry (FAAID)",
      "credentialCategory": "Fellowship",
      "recognizedBy": { "@type": "Organization", "name": "American Academy of Implant Dentistry", "sameAs": "https://aaid-implant.org" }
    },
    {
      "@type": "EducationalOccupationalCredential",
      "name": "Diplomate, International Congress of Oral Implantologists (DICOI)",
      "credentialCategory": "BoardCertification",
      "recognizedBy": { "@type": "Organization", "name": "International Congress of Oral Implantologists", "sameAs": "https://icoi.world" }
    }
  ],
  "worksFor": {
    "@type": "Dentist",
    "name": "Trinity Dental Arts",
    "url": "https://trinitydentalarts.com"
  }
};

/**
 * withDrGjelajSchema(existingSchemas)
 * Helper: appends author + reviewedBy Person node to every schema object in the array.
 */
export function withDrGjelajSchema(schemas = []) {
  return schemas.map(schema => ({
    ...schema,
    author: DR_GJELAJ_PERSON_SCHEMA,
    reviewedBy: DR_GJELAJ_PERSON_SCHEMA,
  }));
}

export default function MedicalByline({ className = '' }) {
  return (
    <div className={`flex items-center gap-2.5 py-2.5 px-4 bg-amber-50 border border-amber-100 rounded-lg text-sm ${className}`}>
      <ShieldCheck className="w-4 h-4 text-amber-600 flex-shrink-0" aria-hidden="true" />
      <span className="text-slate-600">
        Written and medically reviewed by{' '}
        <Link
          to="/best-implant-dentist-trinity-fl"
          className="font-semibold text-amber-700 hover:text-amber-800 hover:underline"
        >
          Dr. Xhoana Gjelaj, DMD
        </Link>
        <span className="text-slate-500"> (DABOI/ID, MAGD)</span>
      </span>
    </div>
  );
}